import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { Breeds } from "@/helpers/enums";
import { QUIZ } from "@/theme/quizTokens";

type BreedOption = {
  label: string;
  group: string;
};

const POPULAR_BREEDS = new Set([
  "Unknown",
  "Mixed breed",
  "Other",
  "Labrador Retriever",
  "Golden Retriever",
  "French Bulldog",
  "German Shepherd",
  "Bulldog",
  "Poodle",
  "Beagle",
  "Rottweiler",
  "Cocker Spaniel",
  "Border Collie",
  "Cavalier King Charles Spaniel",
  "Shih Tzu",
]);

const filter = createFilterOptions<BreedOption>();

type Props = {
  value: string;
  onChange: (breed: string) => void;
  dogName?: string;
};

export function BreedAutocomplete({ value, onChange, dogName }: Props) {
  const [inputValue, setInputValue] = useState("");

  // Build grouped breed options
  const breedOptions = useMemo(() => {
    const allBreeds = Object.values(Breeds);
    const popular: BreedOption[] = [];
    const alphabetical: Record<string, BreedOption[]> = {};

    for (const breed of allBreeds) {
      if (POPULAR_BREEDS.has(breed)) {
        popular.push({ label: breed, group: "Popular Picks" });
      } else {
        // Group by first letter
        const firstLetter = breed.charAt(0).toUpperCase();
        if (!alphabetical[firstLetter]) {
          alphabetical[firstLetter] = [];
        }
        alphabetical[firstLetter].push({ label: breed, group: firstLetter });
      }
    }

    // Sort alphabetically within groups
    popular.sort((a, b) => {
      // Keep Unknown, Mixed breed, Other at very top
      const pinnedOrder = ["Unknown", "Mixed breed", "Other"];
      const aIdx = pinnedOrder.indexOf(a.label);
      const bIdx = pinnedOrder.indexOf(b.label);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.label.localeCompare(b.label);
    });

    // Sort breeds within each letter group and combine
    const sortedLetters = Object.keys(alphabetical).sort();
    const rest: BreedOption[] = [];
    for (const letter of sortedLetters) {
      alphabetical[letter].sort((a, b) => a.label.localeCompare(b.label));
      rest.push(...alphabetical[letter]);
    }

    return [...popular, ...rest];
  }, []);

  // Find current selected option
  const selectedOption = useMemo(() => {
    return breedOptions.find((opt) => opt.label === value) || null;
  }, [breedOptions, value]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: BreedOption | string | null
  ) => {
    let breed = "";

    if (typeof newValue === "string") {
      // freeSolo: user typed and pressed enter
      // Check if it matches a breed, otherwise use "Other"
      const match = breedOptions.find(
        (opt) => opt.label.toLowerCase() === newValue.toLowerCase()
      );
      breed = match ? match.label : "Other";
    } else if (newValue) {
      breed = newValue.label;
    }

    onChange(breed);
    if (breed) {
      setInputValue(breed);
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Autocomplete
        value={selectedOption}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
        options={breedOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => typeof option === "string" ? option : option.label}
        isOptionEqualToValue={(option, val) => option.label === val.label}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          // Always show Popular Picks group when no input
          if (!params.inputValue) {
            return options;
          }

          // Always include "Other" option when typing (even if no matches)
          const otherOption = options.find((opt) => opt.label === "Other");
          if (otherOption && !filtered.find((opt) => opt.label === "Other")) {
            return [...filtered, otherOption];
          }

          return filtered;
        }}
        freeSolo
        noOptionsText={
          <Box
            sx={{
              textAlign: "center",
              py: 2,
              cursor: "pointer",
              "&:hover": { backgroundColor: QUIZ.color.cream },
            }}
            onClick={() => {
              onChange("Other");
              setInputValue("Other");
            }}
          >
            <Typography sx={{ fontSize: 14, color: QUIZ.color.grey, mb: 1 }}>
              Can't find your breed?
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: QUIZ.color.brown,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 2,
                py: 1,
                borderRadius: "8px",
                backgroundColor: QUIZ.color.green,
              }}
            >
              Select "Other"
            </Typography>
          </Box>
        }
        onBlur={(e) => {
          // If user typed something not in list, default to "Other"
          const typed = inputValue.trim();
          if (typed && !breedOptions.find((opt) => opt.label.toLowerCase() === typed.toLowerCase())) {
            onChange("Other");
            setInputValue("Other");
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Start typing a breed"
            sx={{
              // Figma breed input: plain white rounded field, left-aligned grey
              // placeholder, no icon and no dropdown chevron.
              "& .MuiOutlinedInput-root": {
                backgroundColor: QUIZ.color.white,
                borderRadius: { xs: QUIZ.radius.input, md: QUIZ.radius.inputLg },
                border: `1px solid ${QUIZ.color.cream3}`,
                height: { xs: "50px", md: "60px" },
                boxSizing: "border-box",
                boxShadow: "none",
                padding: { xs: "0 21px", sm: "0 21px" },
                transition: "all 0.2s ease",

                "&:hover": {
                  borderColor: QUIZ.color.brown,
                },
                "&.Mui-focused": {
                  borderColor: QUIZ.color.brown,
                },
                // Remove ALL internal borders
                "& fieldset": {
                  display: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  display: "none",
                },
              },
              // Clean input - no borders, no extra styling
              "& .MuiInputBase-input": {
                fontFamily: QUIZ.font.body,
                fontSize: { xs: "17px", sm: "18px" },
                lineHeight: 1.3,
                fontWeight: 400,
                color: QUIZ.color.ink,
                textAlign: "left",
                padding: "0 !important",
                border: "none !important",
                outline: "none !important",
                boxShadow: "none !important",
                background: "transparent !important",
                "&::placeholder": {
                  color: QUIZ.color.placeholder,
                  opacity: 1,
                },
              },
              // Figma shows no chevron / clear affordance on the field
              "& .MuiAutocomplete-endAdornment": {
                display: "none",
              },
              // Remove any input wrapper borders
              "& .MuiInputBase-root": {
                "&::before, &::after": {
                  display: "none",
                },
              },
            }}
          />
        )}
        renderGroup={(params) => (
          <Box key={params.key} component="li" sx={{ listStyle: "none", width: "100%" }}>
            {/* Subtle group label — the Figma panel reads as one flat list */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#f7f6f4",
                px: { xs: 2.5, sm: 3 },
                py: { xs: 1, sm: 1 },
              }}
            >
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 600,
                  color: QUIZ.color.placeholder,
                }}
              >
                {params.group}
              </Typography>
            </Box>
            <Box component="ul" sx={{ p: 0, m: 0, width: "100%" }}>
              {params.children}
            </Box>
          </Box>
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          const isSelected = option.label === value;

          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                width: "100%",
                minHeight: { xs: 44, sm: 42 },
                padding: { xs: "10px 20px", sm: "10px 24px" },
                cursor: "pointer",
                // Figma: selected/active row = solid blue rounded bar
                backgroundColor: isSelected ? QUIZ.color.blue : "transparent",
                borderRadius: isSelected ? "8px" : 0,
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: isSelected ? QUIZ.color.blue : QUIZ.color.cream,
                },
                "&.Mui-focused": {
                  backgroundColor: QUIZ.color.blue,
                  borderRadius: "8px",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 16, sm: 17 },
                  fontWeight: isSelected ? 700 : 400,
                  color: QUIZ.color.ink,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        }}
        slotProps={{
          paper: {
            sx: {
              // Figma dropdown: soft off-white rounded panel with a gentle shadow
              backgroundColor: "#f7f6f4",
              borderRadius: "16px",
              boxShadow: "0 10px 28px rgba(0, 0, 0, 0.14)",
              border: "none",
              mt: 1,
              px: "6px",
              overflow: "hidden",
              width: "100%",
            },
          },
          listbox: {
            sx: {
              maxHeight: { xs: "50vh", sm: "300px" },
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              p: 0,
              width: "100%",
              "& .MuiAutocomplete-option": {
                p: 0,
              },
            },
          },
          popper: {
            sx: {
              zIndex: 1500,
            },
            popperOptions: {
              modifiers: [
                {
                  name: "sameWidth",
                  enabled: true,
                  phase: "beforeWrite",
                  requires: ["computeStyles"],
                  fn: ({ state }: any) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                  },
                },
              ],
            },
          },
        }}
      />
    </Box>
  );
}
